import React from 'react'
import { ClientDataTable } from '../../../ClientDataTable/ClientDataTable'
import CustomCheckbox from '../../../Checkbox/checkbox'
import { format, parseISO } from 'date-fns'
import { IndeterminateCheckbox } from 'components/IndeterminateCheckbox'

export interface Company {
  id: number
  name: string
  last_requested_date?: string
  last_found_date?: string
  selected?: boolean
}

interface SelectableCompaniesTableProps {
  companies: Company[]
  toggleCompanySelection: (companyId: number) => void
  selectAllCompanies: (selected: boolean) => void
  dateColumn: {
    header: string
    accessorKey: string
    dateField: 'last_requested_date' | 'last_found_date'
  }
  enableLogging?: boolean
}

export function SelectableCompaniesTable({
  companies,
  toggleCompanySelection,
  selectAllCompanies,
  dateColumn,
}: SelectableCompaniesTableProps) {
  // No need for internal row selection state - just use props directly
  const allSelected = companies.length > 0 && companies.every((c) => c.selected)

  return (
    <ClientDataTable
      columns={[
        {
          header: 'Company',
          accessorKey: 'name',
          enableSorting: true,
        },
        {
          header: dateColumn.header,
          accessorKey: dateColumn.accessorKey,
          sortingFn: (rowA, rowB) => {
            const dateA = rowA.original[`${dateColumn.accessorKey}_sort`]
            const dateB = rowB.original[`${dateColumn.accessorKey}_sort`]

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            if (dateA instanceof Date && dateB instanceof Date) {
              return dateA.getTime() - dateB.getTime()
            } else {
              // Try to parse as dates if they are strings
              try {
                const parsedA = new Date(dateA as string)
                const parsedB = new Date(dateB as string)
                if (!isNaN(parsedA.getTime()) && !isNaN(parsedB.getTime())) {
                  return parsedA.getTime() - parsedB.getTime()
                }
              } catch {
                // Fallback to string comparison
                return dateA
                  .toLocaleString()
                  .localeCompare(dateB.toLocaleString())
              }
            }
            // Fallback to string comparison
            return dateA.toLocaleString().localeCompare(dateB.toLocaleString())
          },
          enableSorting: true,
        },
        {
          header: () => {
            return (
              <div className="flex justify-center">
                <IndeterminateCheckbox
                  id={`select-all-companies-${dateColumn.accessorKey}`}
                  checked={allSelected}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    selectAllCompanies(e.target.checked)
                  }}
                  indeterminate={
                    companies.some((c) => c.selected) && !allSelected
                  }
                />
                &nbsp;&nbsp;Include?
              </div>
            )
          },
          enableSorting: false,
          accessorKey: 'selected',
          cell: ({ row }) => {
            return (
              <div className="flex justify-center">
                <CustomCheckbox
                  id={`company-${row.original.id}`}
                  checked={row.original.selected}
                  onChange={() => {
                    toggleCompanySelection(row.original.id)
                  }}
                />
              </div>
            )
          },
        },
      ]}
      data={companies.map((company) => {
        const dateValue = company[dateColumn.dateField]
        return {
          id: company.id,
          name: company.name,
          [dateColumn.accessorKey]: dateValue
            ? format(parseISO(dateValue as string), 'MMM d, yyyy')
            : 'N/A',
          [`${dateColumn.accessorKey}_sort`]: dateValue
            ? new Date(dateValue as string)
            : null,
          selected: company.selected,
        }
      })}
      enableRowSelection={false}
      loading={false}
      height={400}
      enableSorting={true}
    />
  )
}
